import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, fileAPI } from '../lib/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../lib/api';
import { getApiUrl } from '../config/api.config';
import FilePreviewModal from '../components/FilePreviewModal';
import PitchEventList from './PitchEventList';
import InvestorsManagement from './InvestorsManagement';
import AlertDialog from '../components/AlertDialog';
import ViewerDashboardTab from '../components/viewer/ViewerDashboardTab';
import ViewerFilesTab from '../components/viewer/ViewerFilesTab';
import ViewerStartupsTab from '../components/viewer/ViewerStartupsTab';

const ViewerDashboard = () => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { tenantId } = useParams();

	// Determine active tab from URL
	const getTabFromPath = () => {
		const path = location.pathname;
		if (path.includes('/files')) return 'files';
		if (path.includes('/startups')) return 'startups';
		if (path.includes('/investors')) return 'investors';
		if (path.includes('/users')) return 'users';
		if (path.includes('/reports')) return 'reports';
		if (path.includes('/events')) return 'events';
		return 'dashboard'; // default
	};

	const [activeTab, setActiveTab] = useState(getTabFromPath());
	const [users, setUsers] = useState([]);
	const [startups, setStartups] = useState([]);
	const [investors, setInvestors] = useState([]);
	const [investorNetworks, setInvestorNetworks] = useState([]);
	const [files, setFiles] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [loading, setLoading] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [previewFile, setPreviewFile] = useState(null);
	const [showUserMenu, setShowUserMenu] = useState(false);
	const userMenuRef = useRef(null);
	const [startupsCurrentPage, setStartupsCurrentPage] = useState(1);
	const startupsPerPage = 10;
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
	
	// Reports data state
	const [reportsLoading, setReportsLoading] = useState(false);
	const [dashboardData, setDashboardData] = useState(null);
	const [investmentData, setInvestmentData] = useState(null);
	const [pitchEventData, setPitchEventData] = useState(null);
	const [userActivityData, setUserActivityData] = useState(null);
	const [reportsActiveTab, setReportsActiveTab] = useState('dashboard');
	const COLORS = useMemo(() => ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'], []);

	// Close user menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
				setShowUserMenu(false);
			}
		};

		if (showUserMenu) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showUserMenu]);

	// Sync activeTab with URL changes
	useEffect(() => {
		setActiveTab(getTabFromPath());
	}, [location.pathname]);

	useEffect(() => {
		loadData();
	}, []);

	// Load reports data when reports tab is active
	useEffect(() => {
		if (activeTab === 'reports') {
			loadAllReports();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	const loadData = async () => {
		setLoading(true);
		try {
			const [usersData, startupsData, investorsData, networksData, filesData] = await Promise.all([
				adminAPI.getAllUsers(),
				adminAPI.getStartups(),
				adminAPI.getInvestors(),
				fetch(getApiUrl('admin/users/investor-networks'), {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				}).then(r => r.json()),
				adminAPI.getAllFiles()
			]);
			setUsers(usersData);
			setStartups(startupsData);
			setInvestors(investorsData);
			setInvestorNetworks(networksData);
			// Sort files by date (most recent first)
			const sortedFiles = [...filesData].sort((a, b) => {
				const dateA = new Date(a.uploadedAt || a.createdAt || 0);
				const dateB = new Date(b.uploadedAt || b.createdAt || 0);
				return dateB - dateA; // Descending order (newest first)
			});
			setFiles(sortedFiles);
		} catch (err) {
			console.error('Failed to load data:', err);
		} finally {
			setLoading(false);
		}
	};

	const loadAllReports = useCallback(async () => {
		setReportsLoading(true);
		try {
			const [dashboard, investments, pitchEvents, userActivity] = await Promise.all([
				api.get('/reports/dashboard').catch(err => {
					console.error('Error loading dashboard report:', err);
					return { data: null };
				}),
				api.get('/reports/investments').catch(err => {
					console.error('Error loading investment report:', err);
					return { data: null };
				}),
				api.get('/reports/pitch-events').catch(err => {
					console.error('Error loading pitch event report:', err);
					return { data: null };
				}),
				api.get('/reports/user-activity?days=30').catch(err => {
					console.error('Error loading user activity report:', err);
					return { data: null };
				})
			]);
			setDashboardData(dashboard.data);
			setInvestmentData(investments.data);
			setPitchEventData(pitchEvents.data);
			setUserActivityData(userActivity.data);
		} catch (error) {
			console.error('Failed to load reports:', error);
		} finally {
			setReportsLoading(false);
		}
	}, []);

	const formatCurrency = (amount) => {
		if (!amount) return '₹0';
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			maximumFractionDigits: 0
		}).format(amount);
	};

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	const openPreview = (file) => {
		setPreviewFile(file);
		setShowPreview(true);
	};

	const closePreview = () => {
		setShowPreview(false);
		setPreviewFile(null);
	};

	// Reports tabs
	const reportsTabs = [
		{ id: 'dashboard', label: 'Dashboard Overview' },
		{ id: 'investments', label: 'Investments' },
		{ id: 'pitch-events', label: 'Pitch Events' },
		{ id: 'user-activity', label: 'User Activity' }
	];

	// KPICard component for reports
	const KPICard = ({ title, value, icon, color = 'blue' }) => (
		<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
			<div className="flex items-center justify-between">
				<div>
					<p className="text-sm font-medium text-gray-600">{title}</p>
					<p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
				</div>
				<div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
					{icon}
				</div>
			</div>
		</div>
	);

	// Render reports dashboard tab
	const renderReportsDashboardTab = () => {
		if (!dashboardData) {
			return (
				<div className="text-center py-12">
					<p className="text-gray-500">No dashboard data available</p>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{/* KPI Cards */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
					<KPICard
						title="Total Users"
						value={dashboardData.totalUsers || 0}
						color="blue"
						icon={
							<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
							</svg>
						}
					/>
					<KPICard
						title="Total Startups"
						value={dashboardData.totalStartups || 0}
						color="green"
						icon={
							<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
						}
					/>
					<KPICard
						title="Total Investors"
						value={dashboardData.totalInvestors || 0}
						color="purple"
						icon={
							<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
							</svg>
						}
					/>
					<KPICard
						title="Total Files"
						value={dashboardData.totalFiles || 0}
						color="yellow"
						icon={
							<svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
							</svg>
						}
					/>
				</div>

				{/* Charts */}
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					{dashboardData.userGrowth && dashboardData.userGrowth.length > 0 && (
						<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
							<ResponsiveContainer width="100%" height={300}>
								<LineChart data={dashboardData.userGrowth}>
									<CartesianGrid strokeDasharray="3 3" />
									<XAxis dataKey="month" />
									<YAxis />
									<Tooltip />
									<Legend />
									<Line type="monotone" dataKey="count" stroke="#3B82F6" name="Users" />
								</LineChart>
							</ResponsiveContainer>
						</div>
					)}

					{dashboardData.startupByStage && dashboardData.startupByStage.length > 0 && (
						<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">Startups by Stage</h3>
							<ResponsiveContainer width="100%" height={300}>
								<PieChart>
									<Pie
										data={dashboardData.startupByStage}
										dataKey="count"
										nameKey="stage"
										cx="50%"
										cy="50%"
										outerRadius={100}
										label
									>
										{dashboardData.startupByStage.map((entry, index) => (
											<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
										))}
									</Pie>
									<Tooltip />
									<Legend />
								</PieChart>
							</ResponsiveContainer>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Render reports investments tab
	const renderReportsInvestmentsTab = () => {
		if (!investmentData) {
			return (
				<div className="text-center py-12">
					<p className="text-gray-500">No investment data available</p>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{/* KPI Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<KPICard
						title="Total Investments"
						value={formatCurrency(investmentData.totalInvestments)}
						color="green"
						icon={
							<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						}
					/>
					<KPICard
						title="Average Investment"
						value={formatCurrency(investmentData.averageInvestment)}
						color="blue"
						icon={
							<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
							</svg>
						}
					/>
					<KPICard
						title="Total Deals"
						value={investmentData.totalDeals || 0}
						color="purple"
						icon={
							<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
							</svg>
						}
					/>
				</div>

				{/* Charts */}
				{investmentData.investmentsByMonth && investmentData.investmentsByMonth.length > 0 && (
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Investments Over Time</h3>
						<ResponsiveContainer width="100%" height={400}>
							<BarChart data={investmentData.investmentsByMonth}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="month" />
								<YAxis />
								<Tooltip formatter={(value) => formatCurrency(value)} />
								<Legend />
								<Bar dataKey="amount" fill="#10B981" name="Investment Amount" />
							</BarChart>
						</ResponsiveContainer>
					</div>
				)}
			</div>
		);
	};

	// Render reports pitch events tab
	const renderReportsPitchEventsTab = () => {
		if (!pitchEventData) {
			return (
				<div className="text-center py-12">
					<p className="text-gray-500">No pitch event data available</p>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{/* KPI Cards */}
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
					<KPICard
						title="Total Events"
						value={pitchEventData.totalEvents || 0}
						color="blue"
						icon={
							<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
							</svg>
						}
					/>
					<KPICard
						title="Total Participants"
						value={pitchEventData.totalParticipants || 0}
						color="green"
						icon={
							<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
							</svg>
						}
					/>
					<KPICard
						title="Upcoming Events"
						value={pitchEventData.upcomingEvents || 0}
						color="yellow"
						icon={
							<svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						}
					/>
					<KPICard
						title="Completed Events"
						value={pitchEventData.completedEvents || 0}
						color="purple"
						icon={
							<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						}
					/>
				</div>

				{/* Charts */}
				{pitchEventData.eventsByStatus && pitchEventData.eventsByStatus.length > 0 && (
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Events by Status</h3>
						<ResponsiveContainer width="100%" height={300}>
							<PieChart>
								<Pie
									data={pitchEventData.eventsByStatus}
									dataKey="count"
									nameKey="status"
									cx="50%"
									cy="50%"
									outerRadius={100}
									label
								>
									{pitchEventData.eventsByStatus.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
									))}
								</Pie>
								<Tooltip />
								<Legend />
							</PieChart>
						</ResponsiveContainer>
					</div>
				)}
			</div>
		);
	};

	// Render reports user activity tab
	const renderReportsUserActivityTab = () => {
		if (!userActivityData) {
			return (
				<div className="text-center py-12">
					<p className="text-gray-500">No user activity data available</p>
				</div>
			);
		}

		return (
			<div className="space-y-6">
				{userActivityData.activityByDay && userActivityData.activityByDay.length > 0 && (
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">User Activity (Last 30 Days)</h3>
						<ResponsiveContainer width="100%" height={400}>
							<LineChart data={userActivityData.activityByDay}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="date" />
								<YAxis />
								<Tooltip />
								<Legend />
								<Line type="monotone" dataKey="logins" stroke="#3B82F6" name="Logins" />
								<Line type="monotone" dataKey="uploads" stroke="#10B981" name="File Uploads" />
							</LineChart>
						</ResponsiveContainer>
					</div>
				)}
			</div>
		);
	};

	// Calculate startups pagination
	const startupsPagination = useMemo(() => {
		const totalPages = Math.ceil(startups.length / startupsPerPage);
		const startIndex = (startupsCurrentPage - 1) * startupsPerPage;
		const endIndex = startIndex + startupsPerPage;
		const currentStartups = startups.slice(startIndex, endIndex);
		return { totalPages, startIndex, endIndex, currentStartups };
	}, [startups, startupsCurrentPage]);

	return (
		<>
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
				{/* Single Row Header with Navigation */}
				<header className="bg-white shadow-sm border-b border-gray-200">
					<div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3">
						<div className="flex justify-between items-center">
							{/* Left: Logo */}
							<div className="flex items-center space-x-4 flex-shrink-0 cursor-pointer" onClick={() => navigate(`/${tenantId}/viewer/dashboard`)}>
								<div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
									<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
									</svg>
								</div>
								<h1 className="text-xl font-bold text-gray-900">iVault</h1>
							</div>

							{/* Center: Navigation Tabs */}
							<nav className="flex space-x-1 flex-1 justify-center" aria-label="Tabs">
								{[
									{ id: 'dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
									{ id: 'files', label: 'Files', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
									{ id: 'investors', label: 'Investors', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
									{ id: 'startups', label: 'Startups', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
									{ id: 'events', label: 'Events', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
									{ id: 'users', label: 'Users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
									{ id: 'reports', label: 'Reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
								].map((tab) => (
									<button
										key={tab.id}
										onClick={() => {
											const path = tab.id === 'dashboard' ? 'dashboard' : tab.id;
											navigate(`/${tenantId}/viewer/${path}`);
										}}
										className={activeTab === tab.id
											? 'px-3 py-2 rounded-lg font-medium text-sm flex items-center space-x-2 transition bg-blue-50 text-blue-600'
											: 'px-3 py-2 rounded-lg font-medium text-sm flex items-center space-x-2 transition text-gray-600 hover:text-gray-900 hover:bg-gray-50'}
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={tab.icon} />
										</svg>
										<span>{tab.label}</span>
									</button>
								))}
							</nav>

							{/* Right: User Profile */}
							<div className="flex items-center gap-3 flex-shrink-0">
								<div className="text-right hidden md:block">
									<p className="text-sm font-medium text-gray-900">{user?.name}</p>
									<p className="text-xs text-gray-500">Viewer</p>
								</div>

								{/* User Profile Dropdown */}
								<div className="relative" ref={userMenuRef}>
									<button
										onClick={() => setShowUserMenu(!showUserMenu)}
										className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center hover:ring-2 hover:ring-blue-300 transition"
									>
										<span className="text-white text-sm font-semibold">
											{user?.name?.charAt(0)?.toUpperCase()}
										</span>
									</button>

									{/* Dropdown Menu */}
									{showUserMenu && (
										<div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
											<button
												onClick={() => {
													setShowUserMenu(false);
													handleLogout();
												}}
												className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
												</svg>
												<span>Logout</span>
											</button>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</header>

				<div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
					{/* Content Area */}
					<div className="bg-white rounded-xl shadow-sm border border-gray-200">
						{(loading ? (
							<div className="text-center py-16">
								<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
								</div>
								<p className="text-lg font-medium text-gray-900">Loading data...</p>
								<p className="text-sm text-gray-500 mt-1">Please wait while we fetch the information</p>
							</div>
						) : activeTab === 'dashboard' ? (
							<ViewerDashboardTab users={users} files={files} startups={startups} investors={investors} />
						) : activeTab === 'files' ? (
							<ViewerFilesTab files={files} openPreview={openPreview} />
						) : activeTab === 'investors' ? (
							<InvestorsManagement readOnly={true} />
						) : activeTab === 'startups' ? (
							<ViewerStartupsTab 
								startups={startups}
								startupsPagination={startupsPagination}
								startupsCurrentPage={startupsCurrentPage}
								setStartupsCurrentPage={setStartupsCurrentPage}
							/>
						) : activeTab === 'events' ? (
							<PitchEventList embedded={true} />
						) : activeTab === 'users' ? (
							<div className="p-6 space-y-6">
								<div>
									<h2 className="text-xl font-semibold text-gray-900">Users</h2>
									<p className="text-sm text-gray-500 mt-1">View all users (Read-only)</p>
								</div>

								<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
									<div className="max-h-screen overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
										<table className="min-w-full divide-y divide-gray-300">
											<thead className="bg-gray-50 sticky top-0">
												<tr>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														User
													</th>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Email
													</th>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Role
													</th>
													<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Status
													</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{users.map((user) => (
													<tr key={user.id} className="hover:bg-gray-50">
														<td className="px-6 py-4 whitespace-nowrap">
															<div className="flex items-center">
																<div className="flex-shrink-0 h-10 w-10">
																	<div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
																		<span className="text-white text-sm font-semibold">
																			{user.name?.charAt(0)?.toUpperCase()}
																		</span>
																	</div>
																</div>
																<div className="ml-4">
																	<div className="text-sm font-medium text-gray-900">{user.name}</div>
																</div>
															</div>
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<div className="text-sm text-gray-900">{user.email}</div>
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
																{user.role}
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
																user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
															}`}>
																{user.isActive ? 'Active' : 'Inactive'}
															</span>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>
						) : null)}
					</div>
				</div>
			</div>
			{/* End of main content */}

			{/* File Preview Modal */}
			{/* End of main content */}

			{/* File Preview Modal */}
			{showPreview && previewFile && (
				<FilePreviewModal
					file={previewFile}
					isOpen={showPreview}
					onClose={closePreview}
				/>
			)}

			{/* Alert Dialog */}
			<AlertDialog
				isOpen={alert.isOpen}
				title={alert.title}
				message={alert.message}
				type={alert.type}
				onClose={() => setAlert({ ...alert, isOpen: false })}
			/>
		</>
	);
};

export default ViewerDashboard;
